import React, {ReactNode} from 'react'
import {TableRowName} from '../../../styledComponents/adminPage/Table'
import PPRadioGroup from '../../shared/forms/PPRadioGroup'
import {DEADLINE_UNIT_OF_RESERVATION_AND_CANCEL, HOURS, RESERVATION_TIME_UNIT_MINUTES} from '../../../CodeMaster'
import {Nullable} from '../../../app/TypeHelpers'
import {ReservationSettings} from './ReservationSettingsComponent'
import PPNumericField from '../../shared/forms/PPNumericField'
import {HKConstants} from '../../../HKConstants'
import PPSelect from '../../shared/forms/PPSelect'
import {Messages} from '../../../Messages'
import { Box, TableRow, TableCell, InputAdornment } from '@mui/material';
import {LeftColumnFlexContainer, LeftRowFlexContainer} from '../../../styledComponents/FlexComponents'
import {StyledLabel} from '../../../styledComponents/StyledTypeDefinitions'
import styled from 'styled-components'
import {numberOrNull, stringOrEmpty} from '../../../stringHelper/stringHelpers'
import {addEmptyData} from '../../../componentHelper/selectHelper'
import equal from 'fast-deep-equal'
import PPErrorLabel from '../../shared/forms/PPErrorLabel'
import EventEmitterExAdmin from '../../shared/pageBaseComponents/EventEmitterExAdmin'
import {isIE} from 'react-device-detect'
import {SelectChangeEvent} from '@mui/material/Select'

interface ReservationReceptionDeadlineSettings {
    unitOfReserveReceptionLimit: DEADLINE_UNIT_OF_RESERVATION_AND_CANCEL
    reserveReceptionLimitDate: Nullable<number>
    reserveReceptionLimitTimeHours: Nullable<number>
    reserveReceptionLimitTimeMinutes: Nullable<number>
}

interface Props extends ReservationReceptionDeadlineSettings {
    eventEmitter: EventEmitterExAdmin
    handleReservationSettingsChange: (reservationSettings: Partial<ReservationSettings>) => void
}

class ReservationReceptionDeadlineComponent extends React.Component<Props> {

    static displayName: string = 'ReservationReceptionDeadlineComponent'

    constructor(props: Readonly<Props>) {
        super(props)

        this.handleUnitOfReserveReceptionLimitChange = this.handleUnitOfReserveReceptionLimitChange.bind(this)
    }

    shouldComponentUpdate(nextProps: Readonly<Props>): boolean {
        return !(this.props === nextProps || equal(this.props, nextProps))
    }

    handleUnitOfReserveReceptionLimitChange(event: React.ChangeEvent<HTMLInputElement>): void {
        const {handleReservationSettingsChange, eventEmitter, ...rest} = this.props
        const unitOfReserveReceptionLimit: DEADLINE_UNIT_OF_RESERVATION_AND_CANCEL =
            DEADLINE_UNIT_OF_RESERVATION_AND_CANCEL.codeToEnumConstants(`${event.target.value}`)

        handleReservationSettingsChange({
            ...rest,
            unitOfReserveReceptionLimit: unitOfReserveReceptionLimit,
        })
    }

    handleNumericValueChange = (target: string) =>
        (event: React.ChangeEvent<{ value: unknown }> | SelectChangeEvent<unknown>) => {
        const {handleReservationSettingsChange, eventEmitter, ...rest} = this.props

        handleReservationSettingsChange({
            ...rest,
            [target]: numberOrNull(`${event.target.value}`),
        })
    }

    renderSpecifyDay(): ReactNode {
        const {unitOfReserveReceptionLimit, reserveReceptionLimitDate} = this.props
        if (unitOfReserveReceptionLimit !== DEADLINE_UNIT_OF_RESERVATION_AND_CANCEL.SPECIFIED_DAY) {
            return null
        }
        return (<>
            <Box marginRight={'15px'}/>
            <DeadlineLabel>
                （当日除く）
            </DeadlineLabel>
            <label>
                <ReserveReceptionLimitDate
                    InputProps={{
                        inputProps: {
                            min: 1,
                            max: HKConstants.VMN.FAC.MAX_RESERVATION_ACCEPTANCE_DEADLINE_DAY,
                        },
                        endAdornment: <LimitInputAdornment position="end">日前まで</LimitInputAdornment>,
                    }}
                    value={stringOrEmpty(reserveReceptionLimitDate)}
                    onChange={this.handleNumericValueChange('reserveReceptionLimitDate')}
                    hint={Messages.HFACA0003.toString()}
                />
            </label>
        </>)
    }

    renderSpecifyTime(): ReactNode {
        const {
            unitOfReserveReceptionLimit, reserveReceptionLimitTimeHours, reserveReceptionLimitTimeMinutes,
        } = this.props

        if (unitOfReserveReceptionLimit !== DEADLINE_UNIT_OF_RESERVATION_AND_CANCEL.SPECIFIED_TIME) {
            return null
        }
        return (<>
            <Box marginLeft={'25px'} marginRight={'auto'}>
                <LeftColumnFlexContainer>
                    <LeftRowFlexContainer>
                        <DeadlineLabel>
                            当日
                        </DeadlineLabel>
                        <label>
                            <LeftRowFlexContainer>
                                <PPSelect
                                    data={addEmptyData(HOURS.values())}
                                    value={stringOrEmpty(reserveReceptionLimitTimeHours)}
                                    onChange={this.handleNumericValueChange('reserveReceptionLimitTimeHours')}
                                />
                                時間
                            </LeftRowFlexContainer>
                        </label>
                        <label>
                            <LeftRowFlexContainer>
                                <PPSelect
                                    data={addEmptyData(RESERVATION_TIME_UNIT_MINUTES.values())}
                                    value={stringOrEmpty(reserveReceptionLimitTimeMinutes)}
                                    onChange={this.handleNumericValueChange('reserveReceptionLimitTimeMinutes')}
                                />
                                分前まで
                            </LeftRowFlexContainer>
                        </label>
                    </LeftRowFlexContainer>
                    <PPErrorLabel
                        eventEmitter={this.props.eventEmitter}
                        eventKeys={['reservationSetting.reserveReceptionLimitTime']}
                    />
                </LeftColumnFlexContainer>
            </Box>
        </>)
    }

    render(): ReactNode {
        return (<>
            <TableRow>
                <TableRowName>予約受付締切日</TableRowName>
                <TableCell>
                    <LeftRowFlexContainer>
                        <PPRadioGroup
                            row
                            data={DEADLINE_UNIT_OF_RESERVATION_AND_CANCEL.values()}
                            value={this.props.unitOfReserveReceptionLimit.value}
                            onChange={this.handleUnitOfReserveReceptionLimitChange}
                        />
                        {this.renderSpecifyDay()}
                        {this.renderSpecifyTime()}
                    </LeftRowFlexContainer>
                </TableCell>
            </TableRow>
        </>)
    }
}

const DeadlineLabel: StyledLabel = styled.label`
    margin-right: 15px;
`

const ReserveReceptionLimitDate = styled(PPNumericField)`
  width: 125px;
`

const LimitInputAdornment = styled(InputAdornment)`
  ${isIE && `
    margin-left: -40px !important;
  `}
`

export default ReservationReceptionDeadlineComponent
